import { useCallback } from 'react';

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import {
  GitHubInitialSyncRequest,
  GitHubInitialSyncResponse,
  GitHubInstallationRequestBody,
  GitHubInstallationRequestResponse,
  GitHubInstallationRequestView,
  GitHubIntegrationView,
  GitHubUnsubscribeRequest,
} from '@this+that/models';
import log from 'loglevel';

import { createApiWithAuth } from '../utils/axios';
import { requireQueryData } from '../utils/requireQueryData';

let githubApi: ReturnType<typeof createApiWithAuth> | null = null;

const getGithubApi = () => {
  if (!githubApi) {
    const baseUrl = import.meta.env.VITE_GITHUB_BASE_URL;
    if (!baseUrl) {
      throw new Error('GitHub base URL is not set');
    }
    githubApi = createApiWithAuth(baseUrl);
  }
  return githubApi;
};

const GITHUB_AUTH_URL_PATH = '/auth-url';
const GITHUB_INITIAL_SYNC_PATH = '/initial-sync';
const GITHUB_UNSUBSCRIBE_PATH = '/unsubscribe';
const GITHUB_INSTALLATION_REQUEST_PATH = '/installation-request';
const GITHUB_INSTALLATION_REQUESTS_PATH = '/installation-requests';

export const getGithubIntegrationUrl = async (
  teamId: string,
): Promise<{ message: string }> => {
  try {
    const { data } = await getGithubApi().get(GITHUB_AUTH_URL_PATH, {
      params: { teamId },
    });
    return data;
  } catch (error) {
    log.error('Error fetching GitHub install URL:', error);
    throw error;
  }
};

export const scheduleGithubInitialSync = async (
  params: GitHubInitialSyncRequest,
): Promise<GitHubInitialSyncResponse> => {
  try {
    const { data } = await getGithubApi().post(
      GITHUB_INITIAL_SYNC_PATH,
      params,
    );
    return data;
  } catch (error) {
    log.error('Error scheduling GitHub initial sync:', error);
    throw error;
  }
};

// setup_action=request: the org owner has to approve first, so there is no
// installation to sync yet — we only record who asked, so the approval webhook can
// find its way back to this team.
export const requestGithubInstallation = async (
  params: GitHubInstallationRequestBody,
): Promise<GitHubInstallationRequestResponse> => {
  try {
    const { data } = await getGithubApi().post(
      GITHUB_INSTALLATION_REQUEST_PATH,
      params,
    );
    return data;
  } catch (error) {
    log.error('Error recording GitHub installation request:', error);
    throw error;
  }
};

// Not a GraphQL query: reading this reconciles against GitHub's outstanding-request
// list, which needs the App's private key — a credential the shared GraphQL Lambda has
// no other reason to hold.
const fetchGithubInstallationRequests = async (
  teamId: string,
): Promise<GitHubInstallationRequestView[]> => {
  const { data } = await getGithubApi().get(GITHUB_INSTALLATION_REQUESTS_PATH, {
    params: { teamId },
  });
  return data?.message ?? [];
};

export const getGithubInstallationRequests = async (
  teamId: string,
): Promise<GitHubInstallationRequestView[]> => {
  try {
    return await fetchGithubInstallationRequests(teamId);
  } catch (error) {
    log.error('Error fetching GitHub installation requests:', error);
    return [];
  }
};

export const disconnectGithubIntegration = async (
  params: GitHubUnsubscribeRequest,
): Promise<void> => {
  try {
    await getGithubApi().post(GITHUB_UNSUBSCRIBE_PATH, params);
  } catch (error) {
    log.error('Error disconnecting GitHub integration:', error);
    throw error;
  }
};

const GET_TEAM_GITHUB_INTEGRATIONS = gql`
  query getTeamGithubIntegrations($teamId: String!) {
    getTeamGithubIntegrations(teamId: $teamId) {
      installationId
      accountLogin
      repositorySelection
      repositories
      teamId
      connectedAt
    }
  }
`;

interface GetTeamGithubIntegrationsData {
  getTeamGithubIntegrations: GitHubIntegrationView[];
}

export interface TeamGithubState {
  integrations: GitHubIntegrationView[];
  installationRequests: GitHubInstallationRequestView[];
}

export function useGetTeamGithubIntegrations() {
  const { loading, refetch } = useQuery<GetTeamGithubIntegrationsData>(
    GET_TEAM_GITHUB_INTEGRATIONS,
    { skip: true },
  );

  const getTeamGithubIntegrations = useCallback(
    async (teamId: string): Promise<TeamGithubState> => {
      try {
        const [result, installationRequests] = await Promise.all([
          refetch({ teamId }),
          fetchGithubInstallationRequests(teamId),
        ]);
        return {
          integrations:
            requireQueryData(result).getTeamGithubIntegrations ?? [],
          installationRequests,
        };
      } catch (error) {
        log.error('Error fetching GitHub integrations:', error);
        throw error;
      }
    },
    [refetch],
  );

  return { getTeamGithubIntegrations, loading };
}
